import React from "react";
import { AutocompleteProps, createFilterOptions } from "@mui/material/Autocomplete";
import TextFieldCustom from "reusable/mui/text-field/TextFieldCustom";
import PlusIcon from '@mui/icons-material/Add'
import Box from "@mui/material/Box";
import { themeConfig } from '@gap/shared/theme';
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ListSubheader from "@mui/material/ListSubheader";
import { useThemeStore } from "theme/ThemeStore";
import { makeAutoObservable } from "mobx";
import { ServiceOption } from "./ServiceAutocomplete";
import { isArray } from "reusable/utils/array-utils";
import variables from "variables/variables";
import Check from '@mui/icons-material/Check'
import { AutocompleteGetTagProps } from "@mui/material/useAutocomplete";

type AutocompleteType = AutocompleteProps<ServiceOption, false, false, true>

interface ServiceAutocompleteParams extends Omit<
   AutocompleteType,
   'options' | 'renderInput' | 'value' | 'onChange'
> {
   services: ServiceOption[],
   value?: string,
   values?: ServiceOption[],
   onChange?: (value?: any) => any,
   onSelectPreconfiguredService?: (service: ServiceOption) => any
   helperText?: string
   error?: boolean
   onNewServiceAdd?: (service: string | ServiceOption[]) => void
   label: string
}

interface AccentedAutocompleteOptionParams {
   props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
   inputValue: string,
   option: ServiceOption,
   selected: boolean
}

export default class ServiceAutocompleteStore {
   props: ServiceAutocompleteParams
   value: ServiceOption | null = null
   values: ServiceOption[]
   filter = createFilterOptions<ServiceOption>()
   maxTags = 2
   maxTagLength = 30

   constructor(props: ServiceAutocompleteParams) {
      this.props = props
      if ('value' in props) {
         this.value = props.value
            ? {
               name: props.value,
               inputValue: props.value,
            }
            : props.services.find(({ name }) => props.value && name === props.value) || null
      }

      if ('values' in props) {
         this.values = props.values || []
      }

      makeAutoObservable(this)
   }

   paperComponent: AutocompleteType['PaperComponent'] = (props) => <Paper {...props} elevation={8} />

   groupOption: AutocompleteType['groupBy'] = (option) => typeof option === 'string' ? '' : option.groupOption || option.category || ''

   triggerFormHandlerOnChange = (value?: string) => {
      this.props.onChange?.({ target: { value: value || '' } })
   }

   onChangeMultiple = (_: any, newValue: unknown) => {
      if (!newValue) return

      const newValues = newValue as ServiceOption[]
      // If new one is added && it's not an existing service
      if (newValues.length > this.values.length && typeof newValues[0] !== 'string' && 'inputValue' in newValues[newValues.length - 1]) {
         const newService = newValues[newValues.length - 1]
         this.props.onNewServiceAdd?.(newService.name)
      } else {
         if (isArray(newValues)) {
            this.values = newValues
         } else {
            this.values = []
         }

         this.props.onNewServiceAdd?.(this.values)
      }
   }

   onChange = (_: any, newValue: ServiceOption | string | (string | ServiceOption)[] | null) => {
      if (Array.isArray(newValue) || !newValue) return

      if (typeof newValue === 'string') {
         this.value = { name: newValue }
         this.triggerFormHandlerOnChange(newValue)
      } else if (newValue && 'inputValue' in newValue) {
         this.value = { name: newValue.inputValue || '' }
         this.triggerFormHandlerOnChange(newValue.inputValue)
      } else {
         this.value = newValue
         this.triggerFormHandlerOnChange(newValue ? newValue.name : undefined)

         if (newValue) {
            this.props.onSelectPreconfiguredService?.(newValue)
         }
      }
   }

   filterOptions: AutocompleteType['filterOptions'] = (options, params) => {
      const filtered = this.filter(options, params)

      const { inputValue } = params

      const isExisting = options.some((option) => inputValue === option.name)
      if (inputValue !== '' && !isExisting) {
         filtered.push({
            inputValue,
            name: inputValue,
         })
      }
      return filtered
   }

   getOptionLabel: AutocompleteType['getOptionLabel'] = (option) => {
      if (typeof option === 'string') {
         return option
      }

      if (option.inputValue) {
         return option.inputValue
      }

      return option.name
   }

   renderInput: AutocompleteType['renderInput'] = (params) => <TextFieldCustom
      {...params}
      autoFocus={this.props.autoFocus}
      label={this.props.label}
      variant="outlined"
      error={this.props.error}
      helperText={this.props.helperText}
   />

   isOptionEqualToValue = (option: ServiceOption, value: ServiceOption) => option.id === value.id

   renderGroup: AutocompleteType['renderGroup'] = ({ group, children, key }) => {
      if (group === '') {
         return children
      }
      let topLevelCategory: string | undefined

      // The first category for the org that created the service will have a prefix that needs to be removed
      const startPattern = `^(first)?([^-]+)?-`
      const [, firstText, orgId] = new RegExp(startPattern, 'g').exec(group) || []
      if (!!firstText) {
         topLevelCategory = orgId === variables.gapOrgId ? 'Gap services' : 'Your services'
      }
      group = group.replace(new RegExp(startPattern, 'g'), '')

      return <Box component='ul' key={key} sx={{
         listStyleType: 'none',
         paddingLeft: '0',
         p: 0,
         m: 0,
         '& + .add-option': {
            borderTop: `1px solid ${themeConfig.infoBox.borderColor}`,
            marginTop: '8px',
         }
      }}>
         {topLevelCategory && <Typography variant="subtitle2" sx={{ pl: '16px', pt: '16px' }}>{topLevelCategory}</Typography>}
         <ListSubheader sx={{ fontSize: '14px' }}>{group}</ListSubheader>
         <Box component='ul' sx={{
            listStyleType: 'none',
            paddingLeft: '0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
         }}>
            {children}
         </Box>
      </Box>
   }

   renderTags = (tagValue: ServiceOption[], getTagProps: AutocompleteGetTagProps) => {
      // Calculate the number of tags to display based on maxTags
      // If maxTags is undefined, display all tags
      const displayedTags = this.maxTags ? tagValue.slice(0, this.maxTags) : tagValue
      const additionalTagsCount = this.maxTags ? tagValue.length - this.maxTags : 0

      return (
         <>
            {displayedTags.map((option, index) => (
               <Chip
                  {...getTagProps({ index })}
                  // Truncate the label if this.maxTagLength is defined and the label exceeds this length
                  label={
                     this.maxTagLength && option.name.length > this.maxTagLength
                        ? `${option.name.slice(0, this.maxTagLength)}...`
                        : option.name
                  }
                  key={option.id}
               />
            ))}
            {/* If there are more tags than this.maxTags, display a "+X more" chip. */}
            {additionalTagsCount > 0 && <Chip label={`+${additionalTagsCount} more`} />}
         </>
      )
   }
}

export function AccentedAutocompleteOption({ props, inputValue, option, selected }: AccentedAutocompleteOptionParams) {
   const themeStore = useThemeStore()
   const opt = typeof option === 'string' ? option : option.name
   const inputIndex = opt.toLowerCase().indexOf(inputValue.toLowerCase())
   const preMatch = opt.substring(0, inputIndex)
   const match = opt.substring(inputIndex, inputIndex + inputValue.length)
   const postMatch = opt.substring(inputIndex + inputValue.length)

   // Destructure key from props to pass it directly
   const { key, ...restProps } = props

   if (!option.id) {
      return <Box
         component='li'
         key={key}
         {...restProps}
         className={[restProps.className, 'add-option'].join(' ')}
         sx={{
            display: 'flex',
            justifyContent: 'center !important',
            gap: '8px',
            paddingY: '12px !important',
            margin: '0',
         }}>
         <PlusIcon sx={{ fontSize: '24px' }} />
         <span style={{ lineHeight: '24px' }}>
            Create service "<strong>{inputValue}</strong>"
         </span>
      </Box>
   }

   return (
      <Box
         component="li"
         key={key}
         {...restProps}
         sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
         }}
      >
         <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Typography sx={{ whiteSpace: 'pre' }}>{ // Option text
               inputValue && inputIndex > -1
                  ? <>
                     <span>{preMatch}</span>
                     <strong>{match}</strong>
                     <span>{postMatch}</span>
                  </>
                  : opt
            }</Typography>
            {option.description && (
               <Typography variant="caption" color={themeStore.theme.palette.primary.light}>
                  {option.description}
               </Typography>
            )}
         </Box>
         {selected && <Check />}
      </Box>
   )
}
